import React from "react";
import { defineMessages, useIntl } from "react-intl"
import { Drawer } from "antd";
import { usePushError } from "./api";
import { UiContext } from "./contexts/ui";
import { cacheStorage, dbStorage, db } from "./db/state";
import { Stream, DB } from "./lib";
import Errors from "./shared/Errors";
import StoreError from "./shared/StoreError";
import DashboardLayout from "./dashboard/DashboardLayout";
import 'antd/dist/reset.css';
import Settings from "./components/Settings";
import { SubscriptionProvider } from "./subscription";
import { API } from "./apiUrl";
import { selectWidgets } from "./db/select";
import { addWidget } from "./db/action";
import { getAvailableVersions, getDefaultVersionId } from "./services/bible";

const messages = defineMessages({
  pageTitle: {
    id: "app.pageTitle",
    description: "Page title displayed in the browser tab.",
    defaultMessage: "New Tab",
  },
});

const App: React.FC = () => {
  const intl = useIntl();
  React.useEffect(() => {
    document.title = intl.formatMessage(messages.pageTitle);
  }, [intl]);

  const [ready, setReady] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [creemSuccess, setCreemSuccess] = React.useState(false);

  const pushError = usePushError();
  const handleError =
    (message: string, showError: boolean) => (error: Error) => {
      pushError({ message });
      console.error(error);
      console.error("Caused by:", error.cause);
      if (showError) setError(true);
    };

  React.useEffect(() => {
    const subscriptions = Promise.all([
      dbStorage
        .then((errors) =>
          Stream.subscribe(
            errors,
            handleError(
              "Cannot save your settings. You may have hit the maximum storage capacity.",
              true,
            ),
          ),
        )
        .catch(
          handleError(
            "Cannot open settings storage. Your settings cannot be loaded or saved.",
            true,
          ),
        ),
      cacheStorage
        .then((errors) =>
          Stream.subscribe(
            errors,
            handleError(
              "Cannot save cache. Start up performance may be degraded.",
              false,
            ),
          ),
        )
        .catch(
          handleError(
            "Cannot open cache. Start up performance may be degraded.",
            false,
          ),
        ),
    ]);

    subscriptions.then(() => {
      setReady(true);
    });

    return () => {
      subscriptions.then(([dbSub, cacheSub]) => {
        if (dbSub) dbSub();
        if (cacheSub) cacheSub();
      });
    };
  }, []);

  const { errors, settings, toggleSettings, toggleErrors } = React.useContext(UiContext);

  // 跨站登录同步：登录成功后把 JWT 静默推送到 inoneyear（11.aliving.stream）
  const syncLogin = (token: string) => {
    if (BUILD_TARGET !== "web") return; // 扩展端不走跨站同步
    try {
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.referrerPolicy = "no-referrer";
      iframe.src = `https://11.aliving.stream/?token=${encodeURIComponent(token)}&sync_from=lumina`;
      const remove = () => iframe.remove();
      iframe.onload = () => setTimeout(remove, 3000);
      setTimeout(remove, 15000);
      document.body.appendChild(iframe);
    } catch {}
  };

  // Handle post-payment / OAuth callbacks from URL params
  React.useEffect(() => {
    const url = new URL(window.location.href);
    const queryCode = url.searchParams.get("code");
    const requestId = url.searchParams.get("request_id");
    const checkoutId = url.searchParams.get("checkout_id");
    const orderId = url.searchParams.get("order_id");
    const subscriptionId = url.searchParams.get("subscription_id");
    const hasGoogleState = url.searchParams.get("state");
    const authError = url.searchParams.get("error");
    const hasToken = !!localStorage.getItem("lumina-jwt");

    // ── Cross-site login sync: accept ?token= pushed from inoneyear (11.aliving.stream) ──
    const tokenParam = url.searchParams.get("token");
    if (tokenParam) {
      try { localStorage.setItem("lumina-jwt", tokenParam); } catch {}
      window.history.replaceState({}, "", "/");
      return;
    }

    // ── Cross-site logout sync: accept ?logout=1 pushed from inoneyear ──
    const logoutParam = url.searchParams.get("logout");
    if (logoutParam) {
      try { localStorage.removeItem("lumina-jwt"); } catch {}
      window.history.replaceState({}, "", "/");
      return;
    }

    // ── Creem post-payment redirect ──
    // Creem sends users to the homepage with its own params:
    //   request_id=verifyCode (our code via Creem's request_id field)
    //   code=verifyCode         (if success_url had it as query param)
    //   order_id / subscription_id / checkout_id (Creem's own)
    const verifyCode = queryCode || requestId;
    if (verifyCode && (orderId || subscriptionId)) {
      const verifyUrl = `${API}/api/checkout/verify?code=${encodeURIComponent(verifyCode)}`;
      fetch(verifyUrl)
        .then(async (r) => {
          const data = await r.json();
          if (data.ok && data.platform === "extension") {
            setCreemSuccess(true);
            return;
          }
          if (data.ok) {
            if (!settings) toggleSettings();
          }
          window.history.replaceState({}, "", "/");
        })
        .catch(() => {
          window.history.replaceState({}, "", "/");
        });
      return;
    }

    // ── Our old checkout callback with checkout_id ──
    if (checkoutId && hasToken) {
      if (!settings) toggleSettings();
      return;
    }

    if (authError) {
      window.history.replaceState({}, "", "/");
      return;
    }

    // ── Google OAuth callback ──
    // Only treat "code" as Google OAuth if there's NO order_id/subscription_id
    // AND we haven't already handled it as a Creem redirect above
    if (!queryCode) return;

    // Google OAuth always has a "state" param for CSRF protection
    if (!hasGoogleState) return;

    fetch(`${API}/api/auth/google?${url.searchParams.toString()}`)
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok || !data.token) throw new Error(data.error || "Google login failed");
        localStorage.setItem("lumina-jwt", data.token);
        syncLogin(data.token);
        window.history.replaceState({}, "", "/");
        if (!settings) toggleSettings();
      })
      .catch(() => {
        window.history.replaceState({}, "", "/");
      });
  }, []);

  // Verse deep-link handling: /bookId/chapter/verse?v=id1,id2
  // Supports both direct URLs (web build) and hash-based URLs (hash router).
  React.useEffect(() => {
    if (!ready) return;

    // ── Resolve path + query from either pathname or hash fragment ──
    const verseRegex = /^\/(\d+)\/(\d+)\/(\d+)$/;
    let match: RegExpExecArray | null = null;
    let searchParams: URLSearchParams;

    // 1) Direct pathname (e.g. https://luminaverse.app/61/3/8?v=ChiRcVs,KJV)
    match = verseRegex.exec(window.location.pathname);
    if (match) {
      searchParams = new URLSearchParams(window.location.search);
    } else {
      // 2) Hash fragment (e.g. https://luminaverse.app/#/61/3/8?v=ChiRcVs,KJV)
      const hash = window.location.hash.replace(/^#/, '');
      const queryIndex = hash.indexOf('?');
      const pathPart = queryIndex >= 0 ? hash.slice(0, queryIndex) : hash;
      const queryPart = queryIndex >= 0 ? hash.slice(queryIndex) : '';
      match = verseRegex.exec(pathPart);
      if (!match) return;
      searchParams = new URLSearchParams(queryPart);
    }

    const bookId = match[1];
    const chapter = match[2];
    const verse = match[3];

    // ── Parse version IDs from query ──
    const vParam = searchParams.get('v') || '';
    const allVersions = getAvailableVersions();
    const validIds = new Set(allVersions.map(v => v.id));
    const urlVersionIds = vParam.split(',').map(s => s.trim()).filter(id => validIds.has(id)).slice(0, 5);

    const locale = DB.get(db, "locale") as string || "en";
    const defaultVersionId = getDefaultVersionId(locale);
    const finalVersionIds = urlVersionIds.length > 0 ? urlVersionIds : [defaultVersionId];

    // ── Ensure Bible Verse widget exists ──
    const widgets = selectWidgets();
    let bibleWidget = widgets.find(w => w.key === 'widget/luminaverse');
    if (!bibleWidget) {
      addWidget('widget/luminaverse');
      const updated = selectWidgets();
      bibleWidget = updated.find(w => w.key === 'widget/luminaverse');
    }

    if (!bibleWidget) return;

    // ── Initialize widget data: manual mode with the specified verse ──
    DB.put(db, `data/${bibleWidget.id}`, {
      mode: 'manual',
      seedOffset: 0,
      manualBook: bookId,
      manualChapter: chapter,
      manualVerse: verse,
      manualVersionIds: finalVersionIds,
      verseFontSize: 16,
      verseFontWeight: 500,
      referenceFontSize: 12,
      referenceFontWeight: 500,
    });

    // ── Clean URL to prevent re-trigger on refresh ──
    window.history.replaceState({}, document.title, '/');
  }, [ready]);

  return (
    <>
      {creemSuccess ? (
        <div style={{
          display: "flex", justifyContent: "center", alignItems: "center",
          minHeight: "100vh", background: "#111827", color: "#f9fafb",
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          textAlign: "center", padding: 24,
        }}>
          <div style={{
            background: "#1f2937", border: "1px solid #374151",
            borderRadius: 16, padding: "48px 32px", maxWidth: 400, width: "100%",
          }}>
            <div style={{
              width: 64, height: 64, borderRadius: "50%", background: "#10b981",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 24px", fontSize: 32,
            }}>✓</div>
            <h1 style={{ fontSize: "1.3em", fontWeight: 600, marginBottom: 8 }}>
              Subscription active
            </h1>
            <p style={{ color: "#9ca3af", lineHeight: 1.6, fontSize: "0.95em", margin: 0 }}>
              Open a new tab — your premium experience is ready.
            </p>
          </div>
        </div>
      ) : (
        <>
          {ready ? (
            <SubscriptionProvider>
              <div className="Dashboard fullscreen">
                <DashboardLayout />
                <Drawer
                  rootStyle={{ padding: 3 }}
                  style={{ width: window.innerWidth < 400 ? '80vw' : '' }}
                  styles={{ body: { overflow: 'auto', scrollbarWidth: 'none' as any, padding: 1 } }}
                  closable={false}
                  placement="left"
                  open={settings}
                  onClose={toggleSettings}
                >
                  {settings ? <Settings /> : null}
                </Drawer>
              </div>
            </SubscriptionProvider>
          ) : null}
          {errors ? <Errors onClose={toggleErrors} /> : null}
          {error ? <StoreError onClose={() => setError(false)} /> : null}
        </>
      )}
    </>
  );
};

export default App;
